import { t } from "@lingui/macro";
import { Article, Eraser, SpinnerGap } from "@phosphor-icons/react";
import { defaultResumeData } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AspectRatio,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
  Separator,
} from "@reactive-resume/ui";
import { cn, templatesList } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useState } from "react";

import { useUser } from "@/client/services/user";
import { useResumeStore } from "@/client/stores/resume";

import { getSectionIcon } from "../shared/section-icon";

export const TemplateSection = () => {
  const { user } = useUser();
  const setValue = useResumeStore((state) => state.setValue);
  const currentTemplate = useResumeStore((state) => state.resume.data.metadata.template);

  const [sampleOpen, setSampleOpen] = useState(false);
  const [loadingFresh, setLoadingFresh] = useState(false);

  const onStartFresh = () => {
    setLoadingFresh(true);

    setValue("metadata.layout", defaultResumeData.metadata.layout);
    setValue("basics", {
      ...defaultResumeData.basics,
      name: user?.name ?? "",
      email: user?.email ?? "",
      picture: {
        ...defaultResumeData.basics.picture,
        url: user?.picture ?? "",
      },
    });
    setValue("sections", defaultResumeData.sections);

    setTimeout(() => setLoadingFresh(false), 300);
  };

  return (
    <section id="template" className="grid gap-y-3">
      <header className="flex items-center gap-x-2">
        {getSectionIcon("template")}
        <h2 className="text-sm font-semibold">{t`Template`}</h2>
      </header>

      {/* Quick Start */}
      <div className="space-y-2">
        <Label className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {t`Quick Start`}
        </Label>

        <div className="space-y-2">
        <button
          type="button"
          onClick={() => setSampleOpen(true)}
          className="flex w-full min-w-0 items-center gap-x-2.5 rounded-lg border border-border/60 bg-background/70 px-2.5 py-2 text-left transition-colors hover:bg-background"
        >
          <Article size={18} className="shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-xs font-medium">{t`See an example`}</p>
            <p className="text-muted-foreground text-[11px] leading-tight">
              {t`Preview a finished resume. Your document stays yours.`}
            </p>
          </div>
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              disabled={loadingFresh}
              className="flex w-full min-w-0 items-center gap-x-2.5 rounded-lg border border-border/60 bg-background/70 px-2.5 py-2 text-left transition-colors hover:bg-background"
            >
              {loadingFresh ? (
                <SpinnerGap size={18} className="shrink-0 animate-spin text-primary" />
              ) : (
                <Eraser size={18} className="shrink-0 text-primary" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium">{t`Start Fresh`}</p>
                <p className="text-muted-foreground text-[11px] leading-tight">
                  {t`Clear sections. Your name and email stay.`}
                </p>
              </div>
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t`Start Fresh?`}</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span>{t`This clears your sections and starts blank, but keeps your name and email. This cannot be undone.`}</span>
                <span className="block text-xs">
                  {t`To keep a backup, go to the`}{" "}
                  <strong className="text-foreground">{t`Import / Export`}</strong>{" "}
                  {t`tab and export your resume as JSON. You can re-import it anytime.`}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
              <AlertDialogAction onClick={onStartFresh}>{t`Start Fresh`}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>

        <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{t`Example resume`}</DialogTitle>
              <DialogDescription>
                {t`A finished page in this template. It will not replace your content.`}
              </DialogDescription>
            </DialogHeader>
            <img
              alt=""
              src={`/templates/jpg/${currentTemplate}.jpg`}
              className="w-full rounded-sm border object-cover object-top shadow-sm"
            />
            <Button variant="outline" onClick={() => setSampleOpen(false)}>
              {t`Back to my resume`}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <Separator />

      {/* Templates */}
      <div className="space-y-2">
        <Label className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
          {t`Templates`}
        </Label>

        <div className="grid w-full min-w-0 grid-cols-2 gap-2 @lg/right:grid-cols-3">
          {templatesList.map((template, index) => (
            <AspectRatio key={template} ratio={1 / 1.4142}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                onClick={() => setValue("metadata.template", template)}
                className={cn(
                  "relative h-full w-full cursor-pointer overflow-hidden rounded-sm bg-white ring-primary transition-all hover:ring-2",
                  currentTemplate === template && "ring-2",
                )}
              >
                <img
                  src={`/templates/jpg/${template}.jpg`}
                  alt={template}
                  loading={index < 6 ? "eager" : "lazy"}
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                />

                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent pb-1.5 pt-7">
                  <p className="text-center text-[11px] font-semibold capitalize text-white">
                    {template}
                  </p>
                </div>
              </motion.div>
            </AspectRatio>
          ))}
        </div>
      </div>
    </section>
  );
};
